import { FC, useCallback } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { EnterpriseAuthImagePanel } from "./component/ImagePanel";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { AuthHeader } from "./component/AuthHeader";
import PasswordStrengthChecker from "./component/passwordChecker/PasswordStrengthChecker";
import {
  emptyResetPasswordValue,
  ResetPasswordValues,
  resetPasswordSchema,
} from "./validation";
import PasswordInput from "./component/EnterprisePasswordInput";
import Button from "./component/EnterpriseButton";
import useFetch from "../../hooks/useFetch";
import { resetPasswordAPI } from "../../API/auth";

export const ResetPasswordPage: FC = () => {
  const navigate = useNavigate();
  const { isLoading, load } = useFetch();
  const token = useSearchParams()[0].get("token") || "";

  const handleResetPasswordButton = useCallback(
    async (values: ResetPasswordValues, resetForm: () => void) => {
      const payload = {
        password: values.password,
        confirmPassword: values.confirmPassword,
      };

      load(resetPasswordAPI(token, payload)).then(() => {
        toast.success("Password reset successfully");
        resetForm();
        navigate("/enterprise/signin");
      });
    },
    [load, navigate, token],
  );

  const {
    errors,
    handleChange,
    handleBlur,
    values,
    touched,
    handleSubmit,
    isValid,
    dirty,
    resetForm,
  } = useFormik({
    initialValues: emptyResetPasswordValue,
    validationSchema: resetPasswordSchema,
    onSubmit: async (submittedValues): Promise<void> => {
      if (submittedValues.password !== submittedValues.confirmPassword) {
        return;
      }

      await handleResetPasswordButton(submittedValues, resetForm);
    },
  });

  const isButtonDisabled =
    !isValid ||
    !dirty ||
    !values.confirmPassword ||
    values.password !== values.confirmPassword;

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <EnterpriseAuthContainer>
        <>
          <AuthHeader
            title="Reset Password"
            subtitle="Type in your new password"
          />

          <form onSubmit={handleSubmit}>
            <PasswordInput
              name="password"
              label="New Password"
              placeholder="••••••••••••"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="new-password"
              error={touched.password ? errors.password : undefined}
            />

            {values.password.length > 0 && (
              <PasswordStrengthChecker password={values.password} />
            )}

            <PasswordInput
              name="confirmPassword"
              label="Confirm Password"
              placeholder="••••••••••••"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.confirmPassword &&
                values.password.length > 0 &&
                values.confirmPassword.length > 0
                  ? errors.confirmPassword
                  : undefined
              }
            />

            <Button
              text="Reset Password"
              className="w-full mt-6"
              type={isButtonDisabled ? "muted" : "primary"}
              disabled={isButtonDisabled || isLoading}
              isLoading={isLoading}
            />
          </form>
        </>
      </EnterpriseAuthContainer>

      <EnterpriseAuthImagePanel />
    </div>
  );
};
